import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type {
  EmailListFacebookCampaign,
  FacebookCampaign,
  FacebookDisplayCreative,
  IFacebook,
} from '@feathr/blackbox';
import { CampaignState } from '@feathr/blackbox';
import type { IWizardStepsCompleted } from '@feathr/components';
import {
  ActionBar,
  Alert,
  Button,
  ButtonValid,
  Spinner,
  Step,
  Steps,
  useSteps,
  Wizard,
} from '@feathr/components';
import { useBlackboxVersion } from '@feathr/extender/hooks';
import { useAccount, useFlags, useStore, useUser } from '@feathr/extender/state';
import { flattenErrors, getIconForAction } from '@feathr/hooks';
import type { ListResponse, Model } from '@feathr/rachis';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import { useReconcileUniqueEmails } from '../AdWizard';
import AdWizardAudienceStep, {
  getEphemeralTargetables,
  getTargetSegments,
  validateStepTargets,
} from '../AdWizard/AdWizardAudienceStep';
import AdWizardBudgetStep, { validateStepBudget } from '../AdWizard/AdWizardBudgetStep';
import createCampaignStore, { CampaignContext } from '../CampaignEditPage.context';
import SaveCampaignButton from '../SaveCampaignButton/index';
import StepGoals, { getGoalSegments, validateStepGoals } from '../StepGoals';
import FacebookCreativesStep, { validateStepCreatives } from './FacebookCreativesStep';
import FacebookGeneralStep, { validateStepGeneral } from './FacebookGeneralStep';
import FacebookReviewStep from './FacebookReviewStep';
import { validate } from './FacebookWizard.utils';

interface IProps {
  campaign: FacebookCampaign;
}

function FacebookWizard({ campaign }: IProps): JSX.Element {
  const account = useAccount();
  const {
    Creatives,
    Billables,
    Events,
    Goals,
    Segments,
    Targetings,
    Targetables,
    FacebookCampaignIntegrations,
  } = useStore();
  const { t } = useTranslation();
  const { noMaxBudget } = useFlags();
  const user = useUser();
  const { isOnProduction } = useBlackboxVersion();
  const { isDraft, isStopped } = campaign;

  const foundStep = /#step(\d+)$/.exec(location.hash);
  const defaultStep = foundStep ? +foundStep[1] - 1 : undefined;
  const [currentStep, setCurrentStep] = useState<number>(defaultStep ?? 0);
  const [completeStep, setCompleteStep] = useState<number>(defaultStep ?? 0);
  const event = Events.get(campaign.get('event'));
  const billing = event.get('billing');
  const billable =
    !event.isPending && billing?.billable_id ? Billables.get(billing.billable_id) : undefined;

  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  const creatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  }) as ListResponse<FacebookDisplayCreative>;

  const integration = account.get('facebook', {} as IFacebook);
  const facebookCampaignIntegration = FacebookCampaignIntegrations.get(campaign.id);

  const emailValidation = useReconcileUniqueEmails({
    targetings: targetings.models,
    campaign,
  });

  const getCompletedStepMap = (): IWizardStepsCompleted => {
    const goalSegments = getGoalSegments(goals.models, Segments);
    const five = validateStepBudget({
      campaign,
      event,
      billable,
      account,
      user,
      noMaxBudget,
      isOnProduction,
    });
    return {
      0: !validateStepGeneral(campaign).length,
      1: !(
        targetings.models.length === 0 ||
        validateStepTargets({
          campaign,
          targetings: targetings.models,
          segments: targetingSegments,
          targetables: [],
          emailValidation,
        }).length
      ),
      2: !validateStepCreatives(campaign, creatives.models).length,
      3: !flattenErrors(five).length,
      4: !validateStepGoals({ goals: goals.models, goalSegments }).length,
      5: true,
    };
  };

  // TODO: fix type here
  const getCurrentStepErrors = (currentStep: number | undefined = undefined): any => {
    const goalSegments = getGoalSegments(goals.models, Segments);
    const five = validateStepBudget({
      campaign,
      event,
      billable,
      account,
      user,
      noMaxBudget,
      isOnProduction,
    });
    const map = {
      0: validateStepGeneral(campaign),
      1: flattenErrors(
        validateStepTargets({
          campaign,
          targetings: targetings.models,
          segments: targetingSegments,
          targetables: [],
          emailValidation,
        }),
      ),
      2: validateStepCreatives(campaign, creatives.models),
      3: flattenErrors(five),
      4: flattenErrors(validateStepGoals({ goals: goals.models, goalSegments })),
      5: [],
    };

    if (typeof currentStep === 'number') {
      return map[currentStep];
    }
    return map;
  };

  function getErrors(step?: number): string[] {
    return getCurrentStepErrors(step);
  }

  const waitFor: () => boolean = () => {
    return (
      !!billing?.billable_id &&
      !!billable &&
      !(
        campaign.isPending ||
        creatives.isPending ||
        event.isPending ||
        facebookCampaignIntegration.isPending ||
        goals.isPending ||
        targetings.isPending ||
        billable.isPending
      )
    );
  };
  useSteps(
    {
      getCompletedStepMap,
      waitFor,
      setCurrentStep,
      setCompleteStep,
      skipToMaxStep: [
        CampaignState.Published,
        CampaignState.Publishing,
        CampaignState.Stopped,
      ].includes(campaign.get('state', CampaignState.Draft)),
    },
    [billable, integration],
  );

  function onNext(): void {
    const nextStep = currentStep + 1;
    setCurrentStep(nextStep);
    if (nextStep > completeStep) {
      setCompleteStep(nextStep);
    }
  }

  function onPrev(): void {
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
  }

  const isLoading =
    event.isPending ||
    targetings.isPending ||
    goals.isPending ||
    creatives.isPending ||
    account?.isPending ||
    (facebookCampaignIntegration.isPending && !facebookCampaignIntegration.isErrored) ||
    completeStep === undefined;

  if (isLoading) {
    return <Spinner />;
  }

  const stepsItems = [
    <Step key={0} stepIndex={0} title={t('General')} />,
    <Step key={1} stepIndex={1} title={t('Audience')} />,
    <Step key={2} stepIndex={2} title={t('Creatives')} />,
    <Step key={3} stepIndex={3} title={t('Budget')} />,
    <Step key={4} stepIndex={4} title={t('Goals')} />,
    <Step key={5} stepIndex={5} title={t('Review')} />,
  ];
  const steps = (
    <Steps
      current={currentStep}
      initialCompleted={completeStep}
      onChange={setCurrentStep}
      usePortal={true}
    >
      {stepsItems}
    </Steps>
  );

  const lastStep = stepsItems.length - 1;
  const targetables = getEphemeralTargetables(targetings.models, Targetables);
  const targetingSegments = getTargetSegments(targetings.models, Segments);

  const grandchildModels: Model[] = [
    ...targetingSegments,
    ...getGoalSegments(goals.models, Segments),
    ...targetables,
  ];
  // Converting observables back to vanilla JavaScript.
  const childModels: Model[] = [
    ...targetings.models.slice(),
    ...creatives.models.slice(),
    ...goals.models.slice(),
  ];
  if (targetables.length) {
    grandchildModels.push(...targetables);
  }
  if (targetingSegments.length) {
    grandchildModels.push(...targetingSegments);
  }

  function buttonValidate(): ICampaignValidationErrors {
    const goalSegments = getGoalSegments(goals.models, Segments);
    return validate(
      {
        account,
        billable,
        campaign,
        creatives: creatives.models,
        event,
        goals: goals.models,
        goalSegments,
        targetables,
        targetings: targetings.models,
        targetingSegments,
        integration: integration,
        emailValidation,
      },
      isOnProduction,
      user,
      noMaxBudget,
    );
  }

  const stepSpecificErrors = getErrors(currentStep);
  const facebookIntegrationErrors = account?.validateFacebookIntegration() ?? [];
  const upsyncError = facebookCampaignIntegration?.getUpsyncErrorData();
  const upsyncErrorMessage = upsyncError ? upsyncError.message : undefined;

  const campaignStore = createCampaignStore({ targetings });
  const publishedOrStopped = [
    CampaignState.Published,
    CampaignState.Publishing,
    CampaignState.Stopped,
  ].includes(campaign.get('state'));

  return (
    <CampaignContext.Provider value={campaignStore}>
      <div data-appcues-campaign={campaign.get('_cls')}>
        {facebookIntegrationErrors.length > 0 &&
          facebookIntegrationErrors.map((error) => (
            <Alert
              description={
                <Trans t={t}>
                  Please{' '}
                  <Button href={error.link} target={'_blank'} type={'link'}>
                    resolve this issue
                  </Button>{' '}
                  to be able to publish your campaign.
                </Trans>
              }
              key={error.message}
              title={error.message}
              type={'danger'}
            />
          ))}
        {upsyncError && (
          <Alert
            description={
              upsyncError.link && (
                <Trans t={t}>
                  Please{' '}
                  <Button href={upsyncError.link} target={'_blank'} type={'link'}>
                    resolve this issue
                  </Button>{' '}
                  and then re-publish your campaign.
                </Trans>
              )
            }
            key={'upsync'}
            title={t('Meta Sync Error: {{- message}}', { message: upsyncErrorMessage })}
            type={'danger'}
          />
        )}
        <ActionBar
          left={
            <>
              <Button
                disabled={currentStep <= 0 || isLoading}
                onClick={onPrev}
                prefix={getIconForAction('previous')}
              >
                {t('Previous')}
              </Button>
              <ButtonValid
                disabled={currentStep === lastStep || isLoading || undefined}
                errors={stepSpecificErrors}
                name={'next_step'}
                onClick={onNext}
                suffix={getIconForAction('next')}
                type={isDraft ? 'primary' : 'secondary'}
              >
                {t('Next')}
              </ButtonValid>
            </>
          }
          right={
            <>
              <SaveCampaignButton
                campaign={campaign}
                childModels={childModels}
                grandchildModels={grandchildModels}
                key={'save'}
                name={'save_changes'}
                shouldChangeState={false}
                showIcon={true}
                type={publishedOrStopped ? 'primary' : 'secondary'}
                validate={publishedOrStopped ? buttonValidate : undefined}
              />
              {(isDraft || isStopped) && (
                <SaveCampaignButton
                  campaign={campaign}
                  childModels={childModels}
                  // Disabled publish if not all steps are completed.
                  disabled={completeStep < 5 || undefined}
                  grandchildModels={grandchildModels}
                  key={'changeState'}
                  name={'publish_or_stop'}
                  shouldChangeState={true}
                  validate={buttonValidate}
                />
              )}
            </>
          }
          usePortal={true}
        />
        <Wizard isFullWidth={true} layout={'horizontal'} steps={steps}>
          {currentStep === 0 && (
            <FacebookGeneralStep
              campaign={campaign as FacebookCampaign | EmailListFacebookCampaign}
              onNext={onNext}
            />
          )}
          {currentStep === 1 && (
            <AdWizardAudienceStep
              campaign={campaign}
              emailValidation={emailValidation}
              targetings={targetings}
            />
          )}
          {currentStep === 2 && (
            <FacebookCreativesStep
              campaign={campaign as FacebookCampaign | EmailListFacebookCampaign}
              creatives={creatives.models}
            />
          )}
          {currentStep === 3 && (
            <AdWizardBudgetStep
              account={account}
              billable={billable}
              campaign={campaign}
              event={event}
              targetings={targetings}
            />
          )}
          {currentStep === 4 && <StepGoals campaign={campaign} goals={goals.models} />}
          {currentStep === 5 && (
            <FacebookReviewStep
              account={account}
              billable={billable}
              campaign={campaign}
              creatives={creatives.models}
              event={event}
              goals={goals.models}
              integration={integration}
              targets={targetings.models}
              validate={validate}
            />
          )}
        </Wizard>
      </div>
    </CampaignContext.Provider>
  );
}

export default observer(FacebookWizard);
